import React from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Paperclip, FileText, FileDown } from 'lucide-react';
import { toast } from 'sonner';
import { getDocumentDownloadUrl } from '@/services/loanDocumentService';
import type { ReconDocument } from '@/services/bankerReconService';

export interface ReconDocumentPopoverProps {
  reconDocuments: ReconDocument[];
  settlementStatementPath: string | null;
  finalCheckPath: string | null;
  payoutType: 'Banked' | 'Brokered' | 'Override' | 'Mentor' | 'Volume Bonus';
}

export const ReconDocumentPopover: React.FC<ReconDocumentPopoverProps> = ({
  reconDocuments,
  settlementStatementPath,
  finalCheckPath,
  payoutType
}) => {
  const hasReconDocs = reconDocuments.length > 0;
  const hasScalarPaths = settlementStatementPath || finalCheckPath;

  if (!hasReconDocs && !hasScalarPaths) {
    return <span className="text-muted-foreground">-</span>;
  }

  const warehouseDocs = reconDocuments.filter(d => d.documentType === 'warehouse_reconciliation');
  const purchaseDocs = reconDocuments.filter(d => d.documentType === 'purchase_advice');

  const openDoc = async (storagePath: string) => {
    try {
      const url = await getDocumentDownloadUrl(storagePath);
      window.open(url, '_blank');
    } catch {
      toast.error('Failed to open document');
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
          <Paperclip className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="end">
        <div className="space-y-1">
          <div className="text-xs font-medium text-muted-foreground px-2 py-1">Documents</div>
          {hasReconDocs ? (
            <>
              {warehouseDocs.length > 0 && (
                <>
                  <div className="text-xs font-medium text-muted-foreground px-2 pt-1">Warehouse Reconciliation</div>
                  {warehouseDocs.map(doc => (
                    <Button key={doc.id} variant="ghost" size="sm" className="w-full justify-start gap-2 h-8" onClick={() => openDoc(doc.storagePath)}>
                      <FileText className="h-4 w-4 shrink-0" />
                      <span className="truncate text-xs">{doc.fileName}</span>
                    </Button>
                  ))}
                </>
              )}
              {purchaseDocs.length > 0 && (
                <>
                  <div className="text-xs font-medium text-muted-foreground px-2 pt-1">Purchase Advice</div>
                  {purchaseDocs.map(doc => (
                    <Button key={doc.id} variant="ghost" size="sm" className="w-full justify-start gap-2 h-8" onClick={() => openDoc(doc.storagePath)}>
                      <FileDown className="h-4 w-4 shrink-0" />
                      <span className="truncate text-xs">{doc.fileName}</span>
                    </Button>
                  ))}
                </>
              )}
            </>
          ) : (
            <>
              {settlementStatementPath && (
                <Button variant="ghost" size="sm" className="w-full justify-start gap-2 h-8" onClick={() => openDoc(settlementStatementPath)}>
                  <FileText className="h-4 w-4" />
                  {payoutType === 'Banked' || payoutType === 'Volume Bonus' ? 'Warehouse Reconciliation' : 'Settlement Statement'}
                </Button>
              )}
              {finalCheckPath && (
                <Button variant="ghost" size="sm" className="w-full justify-start gap-2 h-8" onClick={() => openDoc(finalCheckPath)}>
                  <FileDown className="h-4 w-4" />
                  {payoutType === 'Banked' || payoutType === 'Volume Bonus' ? 'Investor Purchase Advice' : 'Final Check'}
                </Button>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
